import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Languages, ArrowLeft, ArrowRight, Check, Clock, Shield, Award, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

export default function TranslationServices() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const services = [
    {
      id: 'driving_license',
      title: isAr ? 'ترجمة رخصة القيادة' : 'Driving License Translation',
      icon: FileText,
      gradient: 'from-blue-500 to-cyan-500',
      bg: 'bg-blue-500/10',
      color: '#3B82F6',
      description: isAr 
        ? 'خدمات ترجمة احترافية لرخص القيادة، تشمل ترجمات معتمدة ومقبولة من السلطات الإماراتية.'
        : 'Professional driving license translation services, certified and accepted by UAE authorities.',
      features: isAr 
        ? ['ترجمة معتمدة', 'مقبولة رسمياً', 'تسليم سريع', 'جميع اللغات']
        : ['Certified', 'Officially Accepted', 'Fast Delivery', 'All Languages'],
      price: isAr ? 'يبدأ من 150 درهم' : 'From 150 AED'
    },
    {
      id: 'legal_documents',
      title: isAr ? 'ترجمة المستندات القانونية' : 'Legal Document Translation',
      icon: Languages,
      gradient: 'from-indigo-500 to-purple-500',
      bg: 'bg-indigo-500/10',
      color: '#6366F1',
      description: isAr 
        ? 'ترجمة العقود والاتفاقيات والمستندات القانونية من قبل مترجمين قانونيين معتمدين.'
        : 'Translation of contracts, agreements and legal documents by certified legal translators.',
      features: isAr 
        ? ['مترجمين معتمدين', 'دقة قانونية', 'سرية تامة', 'توثيق رسمي']
        : ['Certified Translators', 'Legal Accuracy', 'Confidential', 'Official Attestation'],
      price: isAr ? 'يبدأ من 200 درهم' : 'From 200 AED'
    },
    {
      id: 'certificates',
      title: isAr ? 'ترجمة الشهادات' : 'Certificate Translation',
      icon: Award,
      gradient: 'from-teal-500 to-emerald-500',
      bg: 'bg-teal-500/10',
      color: '#14B8A6',
      description: isAr 
        ? 'ترجمة الشهادات الأكاديمية وشهادات الميلاد والزواج وجميع الشهادات الرسمية.'
        : 'Translation of academic certificates, birth and marriage certificates and all official documents.',
      features: isAr 
        ? ['شهادات أكاديمية', 'شهادات شخصية', 'اعتماد رسمي', 'جميع اللغات']
        : ['Academic Certs', 'Personal Certs', 'Official Attestation', 'All Languages'],
      price: isAr ? 'يبدأ من 100 درهم' : 'From 100 AED'
    },
    {
      id: 'business_docs',
      title: isAr ? 'ترجمة مستندات الأعمال' : 'Business Document Translation',
      icon: Shield,
      gradient: 'from-orange-500 to-amber-500',
      bg: 'bg-orange-500/10',
      color: '#F97316',
      description: isAr 
        ? 'ترجمة التقارير المالية والعقود التجارية ومستندات الشركات.'
        : 'Translation of financial reports, commercial contracts and company documents.',
      features: isAr 
        ? ['تقارير مالية', 'عقود تجارية', 'مستندات شركات', 'سرية مضمونة']
        : ['Financial Reports', 'Commercial Contracts', 'Company Docs', 'Guaranteed Privacy'],
      price: isAr ? 'يبدأ من 250 درهم' : 'From 250 AED'
    }
  ];

  const handleStartService = (service) => {
    // Check login only when user tries to start the service (submit)
    if (!user) {
      // Store the intended service in localStorage for redirect after login
      localStorage.setItem('pendingService', JSON.stringify({
        type: 'translation',
        service: service
      }));
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً للاستفادة من هذه الخدمة' : 'Please login first to use this service');
      navigate('/login', { state: { returnUrl: '/services/translation' } });
      return;
    }
    
    navigate('/services/company-formation/chat', { 
      state: { 
        serviceType: 'translation',
        serviceName: service.title,
        serviceContext: 'translation_services',
        selectedService: service.id,
        serviceInfo: {
          id: service.id,
          title: service.title,
          price: service.price,
          features: service.features
        }
      } 
    });
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-white'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero Section - Compact */}
      <section className={`py-6 ${isDarkMode ? 'bg-[#0d0d0d]' : 'bg-gradient-to-r from-purple-500 to-indigo-600'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <button 
            onClick={() => navigate('/services')}
            className={`flex items-center gap-2 mb-4 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-white/80 hover:text-white'}`}
          >
            {isAr ? <ArrowRight className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
            {isAr ? 'العودة' : 'Back'}
          </button>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-white/20'}`}>
              <FileText className={`h-8 w-8 ${isDarkMode ? 'text-purple-400' : 'text-white'}`} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-white'}`}>
                {isAr ? 'خدمات الترجمة القانونية' : 'Legal Translation Services'}
              </h1>
              <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-white/80'}`}>
                {isAr ? 'ترجمة معتمدة من مترجمين محترفين' : 'Certified translation by professionals'}
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Services Grid */}
      <section className={`py-6 ${isDarkMode ? '' : 'bg-[#f8fafc]'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service) => (
              <div 
                key={service.id}
                className={`group rounded-2xl p-6 transition-all duration-300 hover:-translate-y-2 ${isDarkMode ? 'bg-[#141414] border border-white/5 hover:border-blue-500/30 hover:shadow-lg hover:shadow-blue-500/10' : 'bg-white border-2 border-gray-200 shadow-md hover:shadow-xl hover:border-blue-500/50'}`}
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-5`}>
                  <service.icon className="h-7 w-7 text-white" />
                </div>
                
                <h3 className={`text-xl font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                  {service.title}
                </h3>
                
                <p className={`text-sm mb-5 leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {service.description}
                </p>
                
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                
                <div className={`flex items-center justify-between pt-5 border-t ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`}>
                  <span className="text-blue-500 font-bold">{service.price}</span>
                  <Button 
                    onClick={() => handleStartService(service)}
                    className={`bg-gradient-to-r ${service.gradient} text-white`}
                  >
                    {isAr ? 'ابدأ الخدمة' : 'Start Service'}
                    {isAr ? <ArrowLeft className="h-4 w-4 ms-2" /> : <ArrowRight className="h-4 w-4 ms-2" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className={`text-3xl font-semibold text-center mb-12 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
            {isAr ? 'لماذا تختارنا؟' : 'Why Choose Us?'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className={`text-center p-6 rounded-2xl ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200'}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'سرعة التسليم' : 'Fast Delivery'}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isAr ? 'تسليم الترجمات في أسرع وقت ممكن' : 'Fastest possible translation delivery'}
              </p>
            </div>
            <div className={`text-center p-6 rounded-2xl ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200'}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
                <Shield className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'جودة معتمدة' : 'Certified Quality'}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isAr ? 'ترجمات معتمدة ومقبولة رسمياً' : 'Certified and officially accepted translations'}
              </p>
            </div>
            <div className={`text-center p-6 rounded-2xl ${isDarkMode ? 'bg-[#141414] border border-white/5' : 'bg-white border-2 border-gray-200'}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-blue-500/10' : 'bg-blue-100'}`}>
                <Award className="h-8 w-8 text-blue-500" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'مترجمون خبراء' : 'Expert Translators'}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isAr ? 'فريق من المترجمين المحترفين المعتمدين' : 'Team of certified professional translators'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
